import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { ArrowRight, Leaf, Search, Sparkles, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import ProductCard from '@/components/shared/ProductCard';
import InquiryModal from '@/components/shared/InquiryModal';
import ProductDetailModal from '@/components/shared/ProductDetailModal';
import { getCategories, getProducts, smartSearch, getSearchSuggestions } from '@/lib/api';
import { trackEvent } from '@/hooks/useAnalytics';
import SEO from '@/components/shared/SEO';

export default function ProductsPage() {
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(categoryId || 'all');
  const [showEcoOnly, setShowEcoOnly] = useState(searchParams.get('eco') === 'true');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailProduct, setDetailProduct] = useState(null);

  // TypeSafe AI Smart Search state
  const [searchIntent, setSearchIntent] = useState(null);
  const [smartLoading, setSmartLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [useSmartSearch, setUseSmartSearch] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, prodRes] = await Promise.all([
          getCategories(),
          getProducts(),
        ]);
        setCategories(catRes.data);
        setProducts(prodRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    // Fetch hospitality query suggestions
    getSearchSuggestions()
      .then((res) => setSuggestions(res.data))
      .catch(() => { });
  }, []);

  useEffect(() => {
    if (categoryId) {
      setSelectedCategory(categoryId);
    }
  }, [categoryId]);

  // Debounced Smart Search when searchQuery is present
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchIntent(null);
      return;
    }

    if (!useSmartSearch) return;

    setSmartLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await smartSearch({
          query: searchQuery.trim(),
          category_id: selectedCategory !== 'all' ? selectedCategory : undefined,
          limit: 30,
        });
        if (res.data?.products) {
          setFilteredProducts(res.data.products);
          setSearchIntent(res.data.intent);
          trackEvent('search_query', {
            search_query: searchQuery.trim(),
            path: '/products'
          });
        }
      } catch (err) {
        console.error('Smart search failed, using fallback:', err);
      } finally {
        setSmartLoading(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [searchQuery, selectedCategory, useSmartSearch]);

  // Standard catalogue filter when not performing active smart search
  useEffect(() => {
    if (searchQuery.trim() && useSmartSearch) {
      return;
    }

    let filtered = [...products];

    // Filter by category
    if (selectedCategory && selectedCategory !== 'all') {
      filtered = filtered.filter((p) => p.category_id === selectedCategory);
    }

    // Filter by eco-friendly
    if (showEcoOnly) {
      filtered = filtered.filter((p) => p.is_eco_friendly);
    }

    // Fallback standard text search
    if (searchQuery && !useSmartSearch) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.description.toLowerCase().includes(query)
      );
    }

    // Sort: category display_order first, then product display_order within each category
    const catOrderMap = {};
    categories.forEach((c) => { catOrderMap[c.id] = c.display_order ?? 0; });
    filtered.sort((a, b) => {
      const catDiff = (catOrderMap[a.category_id] ?? 999) - (catOrderMap[b.category_id] ?? 999);
      if (catDiff !== 0) return catDiff;
      return (a.display_order || 0) - (b.display_order || 0);
    });

    setFilteredProducts(filtered);
  }, [products, categories, selectedCategory, showEcoOnly, searchQuery, useSmartSearch]);

  const currentCategory = categories.find((c) => c.id === selectedCategory);

  // Categories filtered by eco availability when eco filter is on
  const visibleCategories = showEcoOnly
    ? categories.filter(cat => products.some(p => p.category_id === cat.id && p.is_eco_friendly))
    : categories;

  // Count products per category (respecting eco filter)
  const getCatCount = (catId) => {
    let pool = products;
    if (showEcoOnly) pool = pool.filter(p => p.is_eco_friendly);
    if (catId === 'all') return pool.length;
    return pool.filter(p => p.category_id === catId).length;
  };

  return (
    <div className="min-h-screen" data-testid="products-page">
      <SEO title="Products" description="Browse 800+ hospitality products - guest amenities, eco packaging, toiletries, cleaning supplies, slippers, and more. Wholesale pricing available." />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-[#FAFAF9]" data-testid="products-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <span className="section-overline text-[#D4AF37]">Our Products</span>
              <h1
                className="text-4xl md:text-5xl font-bold text-primary mt-4"
                style={{ fontFamily: 'Manrope' }}
              >
                {showEcoOnly ? (
                  <>
                    {currentCategory ? currentCategory.name : 'Eco-Friendly Products'}
                    {!currentCategory && <Leaf className="inline w-8 h-8 ml-2 text-green-600" />}
                  </>
                ) : (
                  currentCategory ? currentCategory.name : 'All Products'
                )}
              </h1>
              {showEcoOnly && !currentCategory && (
                <p className="text-muted-foreground mt-2 max-w-xl">
                  Sustainable hospitality supplies that help reduce environmental impact without compromising quality.
                </p>
              )}
              {currentCategory && (
                <p className="text-muted-foreground mt-2 max-w-xl">
                  {currentCategory.description}
                </p>
              )}
            </div>

            {/* Smart Search */}
            <div className="w-full md:w-96 flex flex-col gap-2">
              <div className="relative w-full">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Smart Search (e.g. 'eco bathroom kit for resort')..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-20 rounded-full bg-white shadow-xs border-gray-200 focus:border-amber-500"
                  data-testid="product-search-input"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                  {smartLoading && <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />}
                  {searchQuery && (
                    <button
                      onClick={() => { setSearchQuery(''); setSearchIntent(null); }}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded-full"
                      aria-label="Clear search"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <span title="Powered by TypeSafe AI System One" className="flex items-center px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-semibold cursor-help">
                    <Sparkles className="w-3 h-3 mr-0.5 text-amber-600" /> AI
                  </span>
                </div>
              </div>

              {/* Suggestions chips */}
              {suggestions?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-0.5">
                  <span className="text-[11px] text-muted-foreground self-center mr-0.5 font-medium">Try:</span>
                  {suggestions.slice(0, 3).map((sugg, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSearchQuery(sugg.query)}
                      className="text-[11px] px-2.5 py-0.5 bg-white hover:bg-amber-50 border border-gray-200 hover:border-amber-300 rounded-full text-gray-700 transition-colors flex items-center gap-1 shadow-2xs"
                    >
                      {sugg.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>


      {/* Filters & Products */}
      <section className="py-12 bg-white" data-testid="products-list">
        <div className="max-w-7xl mx-auto px-6 md:px-12">

          {/* ── Mobile: sticky horizontal category strip ── */}
          <div
            className="md:hidden sticky top-16 z-30 bg-white/95 backdrop-blur-sm border-b border-border -mx-6 px-6 py-3 mb-6 overflow-x-auto"
            style={{ scrollbarWidth: 'none' }}
          >
            <div className="flex items-center gap-2 min-w-max">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${selectedCategory === 'all'
                    ? 'bg-primary text-white'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                data-testid="category-pill-mobile-all"
              >
                All ({getCatCount('all')})
              </button>
              {visibleCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${selectedCategory === cat.id
                      ? 'bg-primary text-white'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  data-testid={`category-pill-mobile-${cat.id}`}
                >
                  {cat.name} ({getCatCount(cat.id)})
                </button>
              ))}
            </div>
          </div>

          {/* ── Desktop: sidebar + products ── */}
          <div className="flex gap-8 items-start">

            {/* Left sticky sidebar — desktop only */}
            <aside
              className="hidden md:flex flex-col gap-1 w-52 shrink-0 sticky"
              style={{ top: '5.5rem', maxHeight: 'calc(100vh - 7rem)', overflowY: 'auto' }}
            >
              {/* Eco toggle */}
              <Button
                variant={showEcoOnly ? 'default' : 'outline'}
                className={`rounded-full mb-3 justify-start ${showEcoOnly
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'border-border'
                  }`}
                onClick={() => setShowEcoOnly(!showEcoOnly)}
                data-testid="eco-filter-btn"
              >
                <Leaf className="w-4 h-4 mr-2 shrink-0" />
                Eco-Friendly Only
              </Button>

              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground px-1 mb-1">
                Categories
              </p>

              {/* All */}
              <button
                onClick={() => setSelectedCategory('all')}
                className={`flex items-center justify-between w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${selectedCategory === 'all'
                    ? 'bg-primary text-white'
                    : 'text-foreground hover:bg-muted'
                  }`}
                data-testid="category-pill-all"
              >
                <span>All Products</span>
                <span className={`text-xs ${selectedCategory === 'all' ? 'opacity-75' : 'text-muted-foreground'}`}>
                  {getCatCount('all')}
                </span>
              </button>

              {/* Per-category */}
              {visibleCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center justify-between w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${selectedCategory === cat.id
                      ? 'bg-primary text-white'
                      : 'text-foreground hover:bg-muted'
                    }`}
                  data-testid={`category-pill-${cat.id}`}
                >
                  <span>{cat.name}</span>
                  <span className={`text-xs ${selectedCategory === cat.id ? 'opacity-75' : 'text-muted-foreground'}`}>
                    {getCatCount(cat.id)}
                  </span>
                </button>
              ))}
            </aside>

            {/* Right: top bar + grid */}
            <div className="flex-1 min-w-0">
              {/* Top bar: eco toggle (mobile) + results count */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                {/* Eco toggle — mobile only */}
                <Button
                  variant={showEcoOnly ? 'default' : 'outline'}
                  className={`md:hidden rounded-full ${showEcoOnly
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'border-border'
                    }`}
                  onClick={() => setShowEcoOnly(!showEcoOnly)}
                  data-testid="eco-filter-btn-mobile"
                >
                  <Leaf className="w-4 h-4 mr-2" />
                  Eco-Friendly
                </Button>

                <p className="ml-auto text-sm text-muted-foreground">
                  Showing <strong>{filteredProducts.length}</strong> product{filteredProducts.length !== 1 ? 's' : ''}
                </p>
              </div>

              {/* TypeSafe AI Intent Banner */}
              {searchIntent && (
                <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-amber-500/10 via-emerald-500/10 to-stone-50 border border-amber-200/80 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-xs">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/20 text-amber-700 flex items-center justify-center shrink-0 mt-0.5">
                      <Sparkles className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 px-2 py-0.5 rounded">
                          AI System
                        </span>
                        {searchIntent.require_eco && (
                          <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded flex items-center gap-1">
                            <Leaf className="w-3 h-3" /> Eco Priority ({Math.round(searchIntent.eco_probability * 100)}%)
                          </span>
                        )}
                        <span className="text-[10px] font-medium text-gray-600 bg-white border border-gray-200 px-2 py-0.5 rounded">
                          Tier: {searchIntent.hospitality_tier.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">
                        {searchIntent.summary}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setSearchQuery(''); setSearchIntent(null); }}
                    className="text-xs text-gray-500 hover:text-gray-900 shrink-0 self-end md:self-auto h-8 px-2"
                  >
                    <X className="w-3.5 h-3.5 mr-1" /> Clear AI Filter
                  </Button>
                </div>
              )}

              {/* Products Grid */}
              {loading ? (

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
                  {Array(10)
                    .fill(0)
                    .map((_, i) => (
                      <div key={i} className="aspect-[4/5] rounded-2xl skeleton" />
                    ))}
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-semibold text-primary mb-2" style={{ fontFamily: 'Manrope' }}>
                    No products found
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Try adjusting your filters or search query
                  </p>
                  <Button
                    onClick={() => {
                      setSelectedCategory('all');
                      setShowEcoOnly(false);
                      setSearchQuery('');
                    }}
                    variant="outline"
                    className="rounded-full"
                    data-testid="clear-filters-btn"
                  >
                    Clear all filters
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
                  {filteredProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onInquiry={setSelectedProduct}
                      onViewDetail={setDetailProduct}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#FAFAF9]" data-testid="products-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4" style={{ fontFamily: 'Manrope' }}>
            Can't find what you're looking for?
          </h2>
          <p className="text-muted-foreground mb-6">
            Contact us with your specific requirements and we'll help you find the right products.
          </p>
          <Link to="/contact">
            <Button className="rounded-full bg-primary hover:bg-primary/90 px-8" data-testid="products-contact-btn">
              Contact Us
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />

      {/* Product Detail Modal */}
      <ProductDetailModal
        product={detailProduct}
        isOpen={!!detailProduct}
        onClose={() => setDetailProduct(null)}
        onInquiry={setSelectedProduct}
      />

      {/* Inquiry Modal */}
      <InquiryModal
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        product={selectedProduct}
      />
    </div>
  );
}
